/* eslint-disable react/prop-types */
import React, { useState, useContext } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { modelNameTransformer } from 'pubweb-smokey/dist/utils/modelNameTransformer';
import { formatNumberWithCommas } from 'pubweb-smokey/dist/utils/formatters';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import FavoritingHeart from '@components/Shared/Favoriting/FavoritingHeart';
import CalculatorSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/calculator.svg';
import CubeOpenSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/cube-open.svg';
import FloorplanSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/floorplan.svg';
import PhoneSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/phone.svg';
import Chevron from './Chevron';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { HeadingStyles } from './Heading.styled';
import HomeSpecs from './HomeSpecs';
import Button from '@components/Shared/Buttons/Button';
import StickerLabels from '@components/Shared/StickerLabels/StickerLabels';
import { STICKER_TYPES } from '@utils/stickerUtils';
import { formatPhoneNumberFromNumber } from 'pubweb-smokey/dist/utils/formatters';
import { imageBtnOptions } from '../HomeDetails';

const CalculatorModal = dynamic(
  () => import('../CalculatorModal/CalculatorModal'),
  { ssr: false }
);

const ScheduleVisit = dynamic(
  () => import('@components/HomeDetails/ScheduleVisit/ScheduleVisit'),
  { ssr: false }
);

const PriceDetails = ({ price, priceIntValue }) => {
  const getPriceLabel = () => {
    if (!priceIntValue) {
      return <h5>${formatNumberWithCommas(price)}&apos;s* Before Options</h5>;
    } else if (price.toString().includes('Before') && priceIntValue) {
      return (
        <h5>${formatNumberWithCommas(priceIntValue)}&apos;s* Before Options</h5>
      );
    } else {
      return <h5>{price}* With Options</h5>;
    }
  };

  return price ? (
    <>
      {getPriceLabel()}
      <div className="see-below">
        <span
          tabIndex="0"
          onClick={() => {
            const el = document.getElementById('disclaimer');
            window.scroll({ top: el.offsetTop - 75, left: 0 });
          }}
        >
          See below for complete pricing details
        </span>
      </div>
    </>
  ) : (
    <>
      <p className="error">This home is unavailable in your search area.</p>
      <p>
        Please select a different location from the filter above or{' '}
        <Link href="/find-a-home/">view similar homes in your area</Link>.
      </p>
    </>
  );
};

const Heading = ({
  homeDetails,
  price,
  isInStock,
  onFindRetailerClick,
  onScheduleVisitClick,
  dealerInfo,
  onImageButtonClick,
  isMobile,
  formSubmitted,
  setFormSubmitted,
  showScheduleVisitForm,
  setShowScheduleVisitForm,
}) => {
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [formData, saveFormData] = useState({});
  const favoritesContext = useContext(DXFavoritesContext);
  const storePhone = formatPhoneNumberFromNumber(
    dealerInfo?.dealer?.phoneNumber || ''
  );
  const isCallPricing = (() =>
    homeDetails.priceLabel &&
    homeDetails.priceLabel === 'Contact Us For Pricing')();

  const scrollLearnMore = (e) => {
    e.preventDefault();
    const el = document.getElementById('learn-more');
    window.scroll({ top: el.offsetTop - 75, left: 0 });
  };

  const showCalculatorPrice = () => {
    if (isCallPricing) {
      return 0;
    } else if (homeDetails?.isOnLand) {
      return homeDetails.priceSort;
    } else {
      return price;
    }
  };

  // Add virtual tour or floorplan buttons
  const imageButtons = () => {
    let btns = [];
    if (homeDetails.virtualTours?.[0]?.virtualTourReference) {
      btns.push(imageBtnOptions.virtTour);
    }
    if (homeDetails?.images?.find((i) => i?.imageType === 4)) {
      btns.push(imageBtnOptions.floorplan);
    }
    return btns;
  };

  return (
    <HeadingStyles>
      <div className="home-details-heading">
        {showCalculatorModal && (
          <CalculatorModal
            price={showCalculatorPrice()}
            onClose={setShowCalculatorModal}
          />
        )}

        <div className="home-info">
          {(isInStock || homeDetails.isOnLand) && (
            <div className="sticker-labels">
              <StickerLabels
                stickers={[
                  isInStock && STICKER_TYPES[-7],
                  homeDetails.isOnLand && STICKER_TYPES[1],
                ]}
              />
            </div>
          )}

          <div className="home-title-card">
            <h1 className="h2">
              {modelNameTransformer(
                homeDetails.modelName
                  ? homeDetails.modelName
                  : homeDetails.modelDescription
              )}
            </h1>

            {isCallPricing ? (
              <a
                href={
                  isMobile && storePhone !== ''
                    ? `tel:${storePhone}`
                    : '#learn-more'
                }
                className="call-link"
                onClick={(e) => {
                  !isMobile ? scrollLearnMore(e) : null;
                }}
              >
                <PhoneSvg viewBox="0 0 16 16" className="call-icon" />{' '}
                {homeDetails.priceLabel}
              </a>
            ) : (
              <PriceDetails
                price={price}
                priceIntValue={homeDetails?.priceSort}
              />
            )}

            <div className="home-utilities">
              {imageButtons().includes(imageBtnOptions.floorplan) && (
                <button
                  onClick={() => onImageButtonClick(imageBtnOptions.floorplan)}
                >
                  <FloorplanSvg /> Floor Plan
                </button>
              )}
              {imageButtons().includes(imageBtnOptions.virtTour) && (
                <button
                  onClick={() => onImageButtonClick(imageBtnOptions.virtTour)}
                >
                  <CubeOpenSvg /> 3D Tour
                </button>
              )}
              <button onClick={() => setShowCalculatorModal(true)}>
                <CalculatorSvg /> Mortgage Calculator
              </button>
            </div>
          </div>
        </div>

        <div className="home-specs-container">
          <HomeSpecs homeDetails={homeDetails} />
        </div>
        <ul className="action-buttons">
          <li>
            <Button
              automationId="schedule-visit-click"
              buttonStyle={
                (isMobile && showScheduleVisitForm) || formSubmitted
                  ? 'outlined'
                  : null
              }
              id="schedule-visit-click"
              fillContainer={true}
              onClick={() => {
                isMobile
                  ? setShowScheduleVisitForm(!showScheduleVisitForm)
                  : onScheduleVisitClick();
                pushGTMEvent('requestInfoClick');
              }}
            >
              {isMobile && (
                <Chevron
                  className={`${showScheduleVisitForm ? 'down' : 'up'} ${showScheduleVisitForm || formSubmitted ? 'blue-fill' : 'white-fill'}`}
                />
              )}
              Request Info
            </Button>
          </li>
          <li>
            {isMobile && showScheduleVisitForm && (
              <ScheduleVisit
                homeDetails={homeDetails}
                formOpen={showScheduleVisitForm}
                formData={formData}
                saveFormData={saveFormData}
                onSubmit={() => setFormSubmitted(true)}
                showThankYou={formSubmitted}
              />
            )}
          </li>
          <li>
            <Button
              automationId="find-retailer-click"
              buttonStyle={!formSubmitted ? 'outlined' : null}
              className="find-retailer-button"
              fillContainer={true}
              onClick={() => {
                onFindRetailerClick();
                pushGTMEvent('findRetailerClick');
              }}
            >
              Find A Retailer
            </Button>
          </li>
          <li>
            <Button
              automationId="save-home-click"
              fillContainer={true}
              onDarkBg={true}
              onClick={() => {
                favoritesContext.actions.toggleFavoriteModel(
                  homeDetails.modelNumber
                );
                pushGTMEvent('clickFavoritesHeart');
              }}
            >
              <FavoritingHeart
                isFavorited={
                  !!favoritesContext.state.favoriteModels?.filter(
                    (faveModels) =>
                      faveModels.modelNumber === homeDetails.modelNumber
                  )[0]
                }
                className="icon"
              />
              Save Home
            </Button>
          </li>
        </ul>
      </div>
    </HeadingStyles>
  );
};

export default Heading;
